/*
----------------------------------------------------------
Future Improvement
----------------------------------------------------------

Eligibility editing may be better suited to an explicit
"Save Changes" button rather than auto-save.

Reason:
- Default rule changes require a confirmation dialog.
- Auto-save may trigger the confirmation repeatedly while
  the user is still editing.
- Adding or configuring custom questions often involves
  multiple consecutive edits that should ideally be saved
  together.

Current implementation keeps auto-save for consistency with
WaiverContentEditor, but this can be switched to a manual
save flow later if desired.
*/
import { useEffect, useState, useCallback, useRef } from "react"; ////////NEW 3rd July 2026 useCallback
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";

import API_URL from "../../api/API";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";

import ConfirmRuleChangeModal from "../commoncomponents/ConfirmRuleChangeModal";

import EligibilityRequiredQuestions from "./components/eligibility/EligibilityRequiredQuestions";
import EligibilityCustomQuestions from "./components/eligibility/EligibilityCustomQuestions";

const WaiverEligibilityEditor = () => {

    const { business } = useBusinessAuthContext();
    const location = useLocation();
    const navigate = useNavigate();
    const {
        waiver,refreshChangeSummary,setSaveHandler,setHasUnsavedChanges
    } = useOutletContext();  ////////NEW 3rd July 2026
    const waiverId = waiver?._id;
    const [questions, setQuestions] = useState([]);
    const [customQuestions, setCustomQuestions] = useState([]);
    const [originalQuestions, setOriginalQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const [isDirty, setIsDirty] = useState(false);
    const [modified, setModified] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    //const [ruleChangeConfirmed, setRuleChangeConfirmed] = useState(false);


    useEffect(() => {

        if (!location.state?.showEligibilityConfirm) {
            return;
        }

        setShowConfirm(true);

        /*
        * Clear the navigation state so that refreshing/
        * revisiting the route does not show the popup again.
        */
        navigate(location.pathname, {
            replace: true,
            state: {},
        });

    }, [location, navigate]);

    /*
    ----------------------------------------------------------
    Save Draft
    ----------------------------------------------------------
    */
    const saveDraft = useCallback(async () => {

        if (savingRef.current) return false;

        savingRef.current = true;
        setSaving(true);

        try {

            const res = await fetch(
                `${API_URL.BUSINESS_WAIVER_SMART_STEP_WAIVER_QUESTIONS}/${waiverId}`,
                {
                    method: "PATCH",
                    headers: {
                        Authorization: `Bearer ${business.token}`,
                        "Content-Type": "application/json",
                        "x-session-id": business.sessionId,
                        "x-frontend-url": window.location.pathname,
                    },
                    body: JSON.stringify({
                        questions: [
                            ...questions,
                            ...customQuestions,
                        ],
                    }),
                }
            );

            const data = await res.json();

            if (!data.success) {
                alert(data.message);
                return false;
            }

            await refreshChangeSummary();

            return true;

        } catch (err) {

            console.error(err);
            return false;

        } finally {

            savingRef.current = false;
            setSaving(false);

        }

    }, [
        waiverId,
        questions,
        customQuestions,
        business,
        refreshChangeSummary,
    ]); /* Otherwise it will will still recreate saveHandler every render unless saveDraft is stable. */
    //Notice saving dependency is gone. That is intentional to prevent confirmation popup twice.

    /* Each tab registers its save function*/  ////////NEW 3rd July 2026
    useEffect(() => {
        setSaveHandler(() => saveDraft);
        return () => setSaveHandler(null);
    }, []); /* STOP tracking saveDraft in dependencies - else infintie rendering loop in browser */ 
    useEffect(() => {
        setHasUnsavedChanges(isDirty);
    }, [isDirty]); /* setHasUnsavedChanges DO NOT include setter in deps (it’s stable anyway). */
   

    /*
    ----------------------------------------------------------
    Load Eligibility
    ----------------------------------------------------------
    */

    useEffect(() => {

        if (!waiverId) return;

        const loadEligibility = async () => {

            try {

                const res = await fetch(

                    `${API_URL.BUSINESS_ELIGIBILITY_BY_WAIVER}/${waiverId}`,

                    {

                        headers: {

                            Authorization: `Bearer ${business.token}`,
                            "x-session-id": business.sessionId,
                            "x-frontend-url": window.location.pathname,

                        },

                    }

                );

                const data = await res.json();

                if (!data.success) return;

                const allQuestions = (data.eligibility.questions || []).map(q => ({

                    ...q,

                    question: q.question_adult_full,

                    options: q.options_adult_full || [],

                }));

                setQuestions(
                    allQuestions.filter(q => !q.customQuestion)
                );

                setCustomQuestions(
                    allQuestions.filter(q => q.customQuestion)
                );

                setOriginalQuestions(

                    allQuestions

                        .filter(q => !q.customQuestion)

                        .map(q => ({

                            _id: q._id,

                            options: q.options_org_adult_full || [],

                        }))

                );

            }
            catch (err) {

                console.error(err);

            }
            finally {

                setLoading(false);

            }

        };

        loadEligibility();

    }, [business, waiverId]);

    /*
    ----------------------------------------------------------
    Detect Modified Rules
    ----------------------------------------------------------
    */

    const detectRuleModification = (incomingQuestions) => {

        for (const incoming of incomingQuestions) {

            if (incoming.customQuestion) continue;

            const original = originalQuestions.find(

                q => q._id?.toString() === incoming._id?.toString()

            );

            if (!original) continue;

            const currentOptions = incoming.options || [];

            const originalOptions = original.options || [];

            for (let i = 0; i < currentOptions.length; i++) {

                if (currentOptions[i]?.type !== originalOptions[i]?.type) {

                    return true;

                }

            }

        }

        return false;

    };

    /*
    ----------------------------------------------------------
    Required Question Changed
    ----------------------------------------------------------
    */

    const handleOptionChange = (qIndex, oIndex, newType) => {

        const updated = questions.map((q, qi) => {

            if (qi !== qIndex) return q;

            return {

                ...q,

                options: q.options.map((opt, oi) =>

                    oi === oIndex

                        ? { ...opt, type: newType }

                        : opt

                ),

            };

        });

        setQuestions(updated);

        setModified(

            detectRuleModification(updated)

        );

        setIsDirty(true);

    };

    /*
    ----------------------------------------------------------
    Custom Question Changed
    ----------------------------------------------------------
    */

    const updateCustomQuestions = updated => {

        setCustomQuestions(updated);

        setIsDirty(true);

    };

    /*
    ----------------------------------------------------------
    Auto Save
    ----------------------------------------------------------
    */

    useEffect(() => {

        if (!isDirty) return;

        const timer = setTimeout(async () => {

            const saved = await saveDraft();

            if (!saved) return;

            setIsDirty(false);
            setHasUnsavedChanges(false);

            if (modified) {
                setShowConfirm(true);
            }

        }, 1500);

        return () => clearTimeout(timer);

    }, [isDirty, modified, saveDraft]);

   // }, [questions, customQuestions, isDirty]); //This retriggers too often and can conflict with navigation save.

    
    /*
    ----------------------------------------------------------
    Loading
    ----------------------------------------------------------
    */

    if (loading) {

        return <div>Loading...</div>;

    }

    /*
    ----------------------------------------------------------
    Render
    ----------------------------------------------------------
    */

    return (

        <>

            <div className="max-w-4xl">

                <EligibilityRequiredQuestions

                    questions={questions}

                    saving={saving}

                    handleOptionChange={handleOptionChange}

                />

                <EligibilityCustomQuestions

                    customQuestions={customQuestions}

                    updateCustomQuestions={updateCustomQuestions}

                    saving={saving}

                />

                {modified && (

                    <div className="text-sm text-yellow-700 mt-6">

                        You have modified the default screening rules.
                        This may affect liability coverage.

                    </div>

                )}

            </div>

            <ConfirmRuleChangeModal

                open={showConfirm}

                onConfirm={async () => {

                    setShowConfirm(false);

                    /* setRuleChangeConfirmed(true);
                    
                    await saveDraft();

                    //setModified(false);

                    setIsDirty(false);                    
                    setHasUnsavedChanges(false); ////////NEW 3rd July 2026 */

                      try {

                        const res = await fetch(
                            `${API_URL.BUSINESS_WAIVER_ACCEPT_ELIGIBILITY_CHANGES}/${waiverId}`,
                            {
                                method: "PATCH",
                                headers: {
                                    Authorization: `Bearer ${business.token}`,
                                    "x-session-id": business.sessionId,
                                    "x-frontend-url": window.location.pathname,
                                }
                            }
                        );
                        const data = await res.json();
                        if (!data.success) {
                            alert(data.message || "Failed to confirm eligibility changes.");
                            return;
                        }
                        setModified(false);

                        setIsDirty(false);                    
                        setHasUnsavedChanges(false);

                    } catch (err) {
                        console.error(err);
                        alert("Failed to confirm eligibility changes.");
                    }

                }}

                onCancel={() => {

                    setShowConfirm(false);

                }}

            />

        </>

    );

};

export default WaiverEligibilityEditor;